/* eslint-disable react/require-default-props */
import React from 'react';

import { DEFAULT_COLORS, DEFAULT_STYLING } from './constants';
import type { BarChartData, XAxisConfig } from './types';

interface XAxisRendererProps {
  data: BarChartData[];
  chartBottom: number;
  chartLeft: number;
  chartRight: number;
  barSpacing: number;
  xAxisConfig?: XAxisConfig;
  dimensions?: {
    chartLeft: number;
    chartBottom: number;
  };
  barGeometry?: {
    barSpacing: number;
  };
}

/**
 * XAxisRenderer Component
 *
 * Renders the X-axis baseline, text labels, and icon labels.
 * Text labels follow Typography body2-bold styling (12px, 600 weight).
 * Uses design system color tokens for consistency.
 * Handles both text and icon rendering via foreignObject for icons.
 */
function XAxisRenderer({
  data,
  chartBottom,
  chartLeft,
  chartRight,
  barSpacing,
  xAxisConfig,
  dimensions,
  barGeometry,
}: XAxisRendererProps) {
  const labelType = xAxisConfig?.type ?? 'text';
  const hideLabels = xAxisConfig?.hideLabels ?? false;

  return (
    <g className='XAxisRenderer' role='presentation'>
      {/* X-Axis Baseline (horizontal line at bottom) */}
      <line
        x1={chartLeft}
        y1={chartBottom}
        x2={chartRight}
        y2={chartBottom}
        stroke={DEFAULT_COLORS.axisLine}
        strokeWidth='1'
        className='XAxisRenderer-baseline'
        role='presentation'
        aria-hidden='true'
      />

      {/* X-Axis Text Labels with Typography body2-bold styling (12px, 600 weight) */}
      {labelType === 'text' && !hideLabels && (
        <g
          className='XAxisRenderer-labels'
          role='presentation'
          aria-hidden='true'>
          {data.map((item, index) => {
            const barCenterX = chartLeft + (index + 0.5) * barSpacing;
            const labelY = chartBottom + 12; // 12px below baseline
            const displayLabel = xAxisConfig?.formatter
              ? xAxisConfig.formatter(index, item)
              : item.label;

            return (
              <text
                key={`x-label-text-${item.label}`}
                x={barCenterX}
                y={labelY}
                textAnchor='middle'
                dominantBaseline='hanging'
                className='XAxisRenderer-label XAxisRenderer-label__text'
                fontSize={DEFAULT_STYLING.xAxisLabelFontSize}
                fontWeight={DEFAULT_STYLING.xAxisLabelFontWeight}
                fill={DEFAULT_COLORS.axisLabel}>
                {displayLabel}
              </text>
            );
          })}
        </g>
      )}

      {/* X-Axis Icons Layer (inside SVG using foreignObject) */}
      {labelType === 'icon' &&
        xAxisConfig?.formatter &&
        dimensions &&
        barGeometry && (
          <g className='StackedBarChart-xAxisIconsGroup'>
            {data.map((item, index) => {
              const iconX =
                dimensions.chartLeft + index * barGeometry.barSpacing;
              const barWidth = barGeometry.barSpacing * 0.6;
              const barOffsetX = (barGeometry.barSpacing - barWidth) / 2;
              const centeredX = iconX + barOffsetX;

              return (
                <foreignObject
                  key={`icon-${item.label}`}
                  x={centeredX}
                  y={dimensions.chartBottom + 5}
                  width={barWidth}
                  height={40}
                  className='StackedBarChart-iconForeignObject'>
                  <div className='StackedBarChart-iconWrapper'>
                    {xAxisConfig.formatter?.(index, item)}
                  </div>
                </foreignObject>
              );
            })}
          </g>
        )}
    </g>
  );
}

export default React.memo(XAxisRenderer);
